import useOffClick from '@flight-workspace/common-client/src/hooks/useOffClick';
import classNames from 'classnames/bind';
import { DateFormat } from 'common/types';
import dayjs from 'dayjs';
import {
  IReceivedInternationalResult,
  IScheduleDetail,
  ScheduleKeys,
  SelectTypeKeys,
} from 'list/international/types';
import sum from 'lodash/sum';
import { useState } from 'react';
import styles from './index.module.scss';

const cx = classNames.bind(styles);

export const CARBON_EMISSION_EQUAL_RANGE = 10;

const isGreen = (carbonEmission: number, carbonEmissionAverage: number) =>
  Math.round((carbonEmission / carbonEmissionAverage) * 100) <=
  100 - CARBON_EMISSION_EQUAL_RANGE;

const isYellow = (carbonEmission: number, carbonEmissionAverage: number) =>
  Math.round((carbonEmission / carbonEmissionAverage) * 100) <
  100 + CARBON_EMISSION_EQUAL_RANGE;

export interface ICarbonEmissionAverage {
  directFlightCarbonEmissionItineraryAverage?: {
    [itinerary: string]: number;
  };
  directFlightCarbonEmissionAverage?: number;
}

export const getCarbonEmissionInfo = ({
  schedule,
  carbonEmissionAverages,
}: {
  schedule: IScheduleDetail[];
  carbonEmissionAverages: ICarbonEmissionAverage;
}) => {
  const [firstSchedule, lastSchedule] = [
    schedule[0],
    schedule[schedule.length - 1],
  ];

  const carbonEmissionAverage =
    carbonEmissionAverages.directFlightCarbonEmissionItineraryAverage?.[
      `${dayjs(firstSchedule[ScheduleKeys.startDateTime]).format(
        DateFormat.YMD,
      )}${firstSchedule[ScheduleKeys.startAirport]}${
        lastSchedule[ScheduleKeys.endAirport]
      }`
    ];

  const carbonEmission =
    (schedule.every(({ carbonEmission }) => carbonEmission) &&
      sum(schedule.map(({ carbonEmission }) => carbonEmission))) ||
    undefined;

  return { carbonEmission, carbonEmissionAverage };
};

export const getRoundCarbonEmissionInfo = ({
  schedule,
  carbonEmissionAverages,
}: {
  schedule: IScheduleDetail[][];
  carbonEmissionAverages: ICarbonEmissionAverage;
}) => {
  const carbonEmissionAverage =
    carbonEmissionAverages.directFlightCarbonEmissionAverage;

  const carbonEmission =
    (schedule.every(scheduleItem =>
      scheduleItem.every(({ carbonEmission }) => carbonEmission),
    ) &&
      sum(
        schedule.map(scheduleItem =>
          sum(scheduleItem.map(({ carbonEmission }) => carbonEmission)),
        ),
      )) ||
    undefined;
  return { carbonEmission, carbonEmissionAverage };
};

export const getHasCarbonEmissionAverageInfo = (
  orgFlights: IReceivedInternationalResult | undefined,
) =>
  !!orgFlights?.results.carbonEmissionAverage.directFlightCarbonEmissionAverage;

export const carbonEmissionFareFilter =
  (
    hasCarbonEmissionAverageInfo: boolean,
    orgFlights: IReceivedInternationalResult | undefined,
    selectType: SelectTypeKeys,
  ) =>
  (itineraries: string) => {
    if (!hasCarbonEmissionAverageInfo) return false;

    const hasAllCarbonEmissionInfo = itineraries
      .split('|')
      .every((itinerary, itineraryIndex) =>
        orgFlights?.results.schedules[itineraryIndex]?.[
          itinerary
        ]?.detail?.every(({ carbonEmission }) => carbonEmission),
      );
    if (!hasAllCarbonEmissionInfo) return false;
    if (selectType === SelectTypeKeys.concurrent) {
      const totalCarbonEmission = sum(
        itineraries
          .split('|')
          .flatMap((itinerary, itineraryIndex) =>
            orgFlights?.results.schedules[itineraryIndex]?.[
              itinerary
            ].detail.map(({ carbonEmission }) => carbonEmission),
          ),
      );

      return isGreen(
        totalCarbonEmission,
        orgFlights?.results.carbonEmissionAverage
          .directFlightCarbonEmissionAverage as number,
      );
    }
    return itineraries.split('|').every((itinerary, itineraryIndex) => {
      const detail =
        orgFlights?.results.schedules[itineraryIndex]?.[itinerary].detail;
      if (!detail) return false;

      const { carbonEmission, carbonEmissionAverage } = getCarbonEmissionInfo({
        schedule: detail,
        carbonEmissionAverages: orgFlights?.results?.carbonEmissionAverage,
      });
      return (
        carbonEmission &&
        carbonEmissionAverage &&
        isGreen(carbonEmission, carbonEmissionAverage)
      );
    });
  };

enum CarbonEmissionRatio {
  GREEN = 'high',
  YELLOW = 'equal',
  RED = 'low',
}

export function Emissions({
  clickCodePrefix,
  carbonEmission,
  carbonEmissionAverage,
}: {
  clickCodePrefix: string;
  carbonEmission?: number;
  carbonEmissionAverage: number;
}) {
  const [isOpened, setOpened] = useState(false);
  const onClose = () => setOpened(false);
  const ref = useOffClick(onClose, { stopPropagation: true });

  if (!carbonEmission)
    return (
      <div className={cx('Emissions')} data-average="equal">
        <button
          className={cx('summary')}
          type="button"
          data-event-nlog="click"
          data-event-area-code={`${clickCodePrefix}.co2info`}
          onClick={() => setOpened(!isOpened)}
        >
          CO<sub>2</sub> <b className={cx('value')}>-</b>
        </button>
        {isOpened && (
          <div className={cx('detail')} role="tooltip" ref={ref}>
            <b className={cx('title')}>이산화탄소 배출량 정보가 없습니다.</b>
            <p className={cx('desc')}>
              항공편의 예상 CO2 배출량을 비교하여 지속가능한 선택을 할 수
              있습니다. 항공 탄소배출량은 비행 연료 소비량, 스케쥴, 항공기종
              등을 바탕으로 계산됩니다.
            </p>
            <cite className={cx('cite')}>Powered by OAG</cite>
            <button
              className={cx('close')}
              type="button"
              data-event-nlog="click"
              data-event-area-code={`${clickCodePrefix}.co2close`}
              onClick={onClose}
            />
          </div>
        )}
      </div>
    );

  const carbonEmissionRatio = Math.round(
    (carbonEmission / carbonEmissionAverage) * 100,
  );
  const carbonEmissionDiffPercent = Math.abs(carbonEmissionRatio - 100);

  const carbonEmissionGrade = isGreen(carbonEmission, carbonEmissionAverage)
    ? CarbonEmissionRatio.GREEN
    : isYellow(carbonEmission, carbonEmissionAverage)
      ? CarbonEmissionRatio.YELLOW
      : CarbonEmissionRatio.RED;

  return (
    <div className={cx('Emissions')} data-average={carbonEmissionGrade}>
      <button
        className={cx('summary')}
        type="button"
        data-event-nlog="click"
        data-event-area-code={`${clickCodePrefix}.co2info`}
        onClick={() => setOpened(!isOpened)}
      >
        CO<sub>2</sub>{' '}
        {carbonEmissionGrade === CarbonEmissionRatio.YELLOW ? (
          '평균배출'
        ) : (
          <b className={cx('value')}>{carbonEmissionDiffPercent}%</b>
        )}
      </button>
      {isOpened && (
        <div className={cx('detail')} role="tooltip" ref={ref}>
          <b className={cx('title')}>
            이산화탄소 배출량이{' '}
            {carbonEmissionGrade === CarbonEmissionRatio.YELLOW
              ? '평균인'
              : carbonEmissionGrade === CarbonEmissionRatio.GREEN
                ? '적은'
                : '많은'}{' '}
            항공편입니다
          </b>
          <strong className={cx('text')}>
            {carbonEmissionGrade === CarbonEmissionRatio.YELLOW ? (
              <>노선 평균과 유사</>
            ) : (
              <>
                노선 평균 대비{' '}
                <b className={cx('value')}>{carbonEmissionDiffPercent}%</b>{' '}
                {carbonEmissionGrade === CarbonEmissionRatio.GREEN
                  ? '적음'
                  : '많음'}
              </>
            )}
          </strong>
          <p className={cx('desc')}>
            항공편의 예상 CO2 배출량을 비교하여 지속가능한 선택을 할 수
            있습니다. 항공 탄소배출량은 비행 연료 소비량, 스케쥴, 항공기종 등을
            바탕으로 계산됩니다.
          </p>
          <cite className={cx('cite')}>Powered by OAG</cite>
          <button
            className={cx('close')}
            type="button"
            data-event-nlog="click"
            data-event-area-code={`${clickCodePrefix}.co2close`}
            onClick={onClose}
          />
        </div>
      )}
    </div>
  );
}

export default Emissions;
